import { computed } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import {
  buildNavigationCtx,
  failNavigation,
  resolveNextDetails,
  resolveTransition,
  type RouteName,
} from '@eu/app/router/appModule/navigation'
import type { EuRoute } from '@eu/app/router/appModule/routes'
import { configureEmbeddedAssessment } from '@eu/app/router/appModule/embeddedAssessment'

/** Per-route navigateNext argument: intent keys for branches, otherwise void.
 * Most views are JavaScript SFCs, so their calls are validated at runtime. */
type RouteIntentArgs = {
  [R in EuRoute as R['name']]: R extends {
    readonly meta: { readonly next: infer Nx }
  }
    ? Nx extends readonly unknown[]
      ? void
      : keyof Nx & string
    : void
}

type IntentArg<N extends RouteName> = RouteIntentArgs[N & keyof RouteIntentArgs]

/**
 * Resolves navigation from route metadata. The first route determines intent
 * typing; additional routes allow the same view to serve multiple records.
 */
export default function useNavigation<N extends RouteName>(
  routeName: N,
  ...moreRouteNames: RouteName[]
) {
  const route = useRoute()
  const router = useRouter()

  const assertAnchor = () => {
    if (!import.meta.env.DEV) return
    const anchors: RouteName[] = [routeName, ...moreRouteNames]
    if (!anchors.includes(route.name as RouteName)) {
      console.warn(
        `[navigation] useNavigation(${anchors.join(', ')}) called on ` +
          `route "${String(route.name)}" — the intent typing does not ` +
          'cover this route',
      )
    }
  }

  const push = (name: string | null, query: LocationQuery) => {
    if (name == null) return
    return router.push({ name, query })
  }

  const navigateNext = (intent: IntentArg<N>) => {
    assertAnchor()
    const transition = resolveNextDetails(
      String(route.name),
      route.meta?.next,
      intent as string | undefined,
      buildNavigationCtx(),
    )
    if (transition?.assessment) {
      configureEmbeddedAssessment(
        transition.assessment.finishRoute,
        transition.assessment.progressFrom,
        route.query,
      )
    }
    return push(transition?.target ?? null, route.query)
  }

  /** In-app entry routes have no in-flow predecessor. */
  const resolveBack = (): string | null => {
    const ctx = buildNavigationCtx()
    if (ctx.isFromApp && ctx.startRoute === route.name) return null
    return resolveTransition(route.meta?.back, ctx)
  }

  /** `query` overrides the preserved query for screens that must rewrite it. */
  const navigateBack = (options: { query?: LocationQuery } = {}) => {
    assertAnchor()
    const target = resolveBack()
    if (target == null) {
      failNavigation(
        `Route "${String(route.name)}" has no back target for the current ctx`,
      )
      return
    }
    return push(target, options.query ?? route.query)
  }

  const canGoBack = computed(() => resolveBack() != null)

  return { navigateNext, navigateBack, canGoBack }
}
